import { useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { AnnotationsContextProvider } from 'src/components/annotations/AnnotationsContext';
import { JobDetailsPrintOptions } from 'src/components/job-details/information/print/JobDetailsPrint';
import { appBarActions } from 'src/redux/appBar/actions';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { useParsedLog } from 'src/redux/log/thunk/fetchLog';
import type { AppState } from 'src/redux/types';
import { downloadLogFile } from 'src/utils/api/logsApi';
import { useSocket, useSocketGet } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import {
  JobLog,
  JobLogDisplayPermissions,
  JobLogPermission,
  ShareViaEmailRequest,
  SHORT_EXPIRATION_IN_DAYS,
} from '../../models/jobLog';
import {
  canDelete,
  canDownloadLog,
  canManageAnnotations,
  canPrintReport,
  canSeeAnnotations,
  canSeeIdleVsPressure,
  canSeePieCharts,
  canSeePressures,
  canSeeTimestamps,
  canSeeTubeGraph,
  canShare,
  canShowAnnotationUser,
  usePolicy,
} from '../../models/policies';
import { deleteLog } from '../../utils/api/deleteLog';
import { BUNDLE_DOWNLOAD_MINIMUM_FIRMWARE_VERSION } from '../../utils/feature-flags/FeatureFlagConstants';
import { compareFirmwareVersions } from '../../utils/helpers/firmwareHelpers';
import { AppRouteConstants } from '../app-layout/AppRouteConstants';
import { MobileLogListPageActions } from '../jobs/MobileLogsListPage';
import { JobInfo } from './JobInfo';
import type { JobInfoTimeRange } from './types';
import { getPrintQueryParams, useLogCreatedInfo } from './util';
import { canDeleteJobLog, canUpdateJobLog } from 'src/utils/permissions/logs';
import { useGA4 } from '../../utils/ga4';
import { FamilyOrgShareFormData } from '../../components/share-report-modal/FamilyOrgShareForm';
import { GuestShareFormData } from '../../components/share-report-modal/GuestShareForm';
import { useLimitedMode } from '../../utils/auth/useLimitedMode';
import { sendEventUserSharedJobReport, sendEventUserUnsharedEmailShare } from '../../utils/api/auditEventsApi';

// 1000 milliseconds per second times 60 seconds per minute time 60 minutes in an hour time 24 hours in a day.
const MILLISECONDS_IN_ONE_DAY = 1000 * 60 * 60 * 24;

const useDisplayPermissions = (logInfo?: JobLog) => {
  const { item: controller } = useSocketGet(SocketContextSentinel, 'controllers', logInfo?.serialNumber);
  const { item: firmware } = useSocketGet(SocketContextSentinel, 'firmware', controller?.fw);
  const isFirmwareV2OrAbove = compareFirmwareVersions(firmware?.version, BUNDLE_DOWNLOAD_MINIMUM_FIRMWARE_VERSION) >= 0;

  const showTimestamps = usePolicy(canSeeTimestamps, logInfo);
  const showPressures = usePolicy(canSeePressures, logInfo);
  const showPieCharts = usePolicy(canSeePieCharts, logInfo);
  const showIdleVsPressure = usePolicy(canSeeIdleVsPressure, logInfo);
  const showTubeGraph = usePolicy(canSeeTubeGraph, logInfo);
  const showAnnotations = usePolicy(canSeeAnnotations, logInfo);
  const showDeleteActions = usePolicy(canDelete, logInfo);
  const showShareActions = usePolicy(canShare, logInfo);
  const showAnnotationUser = usePolicy(canShowAnnotationUser, logInfo);
  const showAnnotationActions = usePolicy(canManageAnnotations, logInfo);
  const showDownloadActions = usePolicy(canDownloadLog, logInfo);
  const showPrintActions = usePolicy(canPrintReport, logInfo);
  const showEstimatedTimeToCompletion = isFirmwareV2OrAbove && !!logInfo?.status && logInfo.status !== 'complete';

  return useMemo<JobLogDisplayPermissions>(
    () => ({
      showTimestamps,
      showPressures,
      showPieCharts,
      showIdleVsPressure,
      showTubeGraph,
      showAnnotations,
      showDeleteActions,
      showShareActions,
      showAnnotationUser,
      showAnnotationActions,
      showDownloadActions,
      showPrintActions,
      showEstimatedTimeToCompletion,
    }),
    [
      showAnnotationActions,
      showAnnotationUser,
      showAnnotations,
      showDeleteActions,
      showDownloadActions,
      showEstimatedTimeToCompletion,
      showIdleVsPressure,
      showPieCharts,
      showPressures,
      showPrintActions,
      showShareActions,
      showTimestamps,
      showTubeGraph,
    ],
  );
};

export const JobInfoByIdPage: React.FC = () => {
  document.title = 'Job Detail'; // Purposely English only for Google Analytics
  useGA4();

  const history = useHistory();
  const params = useParams<{ id: string }>();
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const userData = useSelector(selectAuthUserInfo);
  const [timeRange, setTimeRange] = useState<JobInfoTimeRange>({ min: 0, max: 1 });
  const onTimeRangeChange = useCallback((tr: JobInfoTimeRange) => {
    setTimeRange(prev => {
      if (prev.min === tr.min && prev.max === tr.max) return prev;
      return tr;
    });
  }, []);

  const { request: logsRequest, update: updateLog } = useSocket(SocketContextSentinel, 'logs');
  const { item: logInfo } = useSocketGet(SocketContextSentinel, 'logs', params.id);
  const { item: org } = useSocketGet(SocketContextPortal, 'organizations', logInfo?.org);
  const { request: sentinelRequest } = useSocket(SocketContextSentinel, 'logs');

  const { isLimited } = useLimitedMode();
  const isLimitedMode = !logInfo?._id || isLimited;
  const canUpdateLog = canUpdateJobLog(userData, logInfo);
  const canDeleteLog = canDeleteJobLog(userData, logInfo);

  const displayPermissions = useDisplayPermissions(logInfo);

  const { abortCtrl, data } = useParsedLog(logInfo?._id, logInfo?.s3?.ETag);

  const formattedName = useLogCreatedInfo(logInfo);
  const user = useSelector(selectAuthUserInfo);

  useIonViewWillEnter(() => {
    dispatch(appBarActions.showBack(true));
  }, [dispatch]);

  useIonViewDidLeave(() => {
    abortCtrl.current?.abort();
  }, []);

  const loadProgress = React.useMemo(() => {
    if (data?.totalLines === undefined) return 0;
    if (data.parsedLines === data.totalLines) return 1;
    return Math.max(0.1, data.parsedLines / data.totalLines);
  }, [data?.parsedLines, data?.totalLines]);

  const showComplete = logInfo?.status === 'in-progress';
  const onComplete = useMemo(() => {
    if (!showComplete) return undefined;

    return async () => {
      try {
        await sentinelRequest('logs live-close-v2', { id: params.id });
      } catch (e) {
        console.error('Could not complete in-progress job log:', e);
        throw e;
      }
    };
  }, [params.id, sentinelRequest, showComplete]);

  // Can only delete completed jobs, owned by your org, if you are a manager. If offline, cannot delete.
  const showDelete = canDeleteLog && !isLimitedMode;
  const onDeleteLog = useMemo(() => {
    if (!showDelete) return undefined;
    return async () => {
      try {
        if (!params?.id) {
          throw new Error('Cannot delete log, no id is provided');
        }

        await deleteLog(params.id);
        history.replace(AppRouteConstants.LogsList, { action: MobileLogListPageActions.DeleteSuccessful });
      } catch (e) {
        console.error('Failed to delete log:', e);
        throw e;
      }
    };
  }, [history, params.id, showDelete]);

  const showShare = displayPermissions.showShareActions;
  const onFamilyOrgShare = useMemo(() => {
    if (!showShare) return undefined;
    return async (values: FamilyOrgShareFormData, orgName: string | undefined) => {
      try {
        if (!orgName) throw new Error('Invalid organization value provided during job log share');

        const request: { id: string; permissions: JobLogPermission[] } = {
          id: params.id,
          permissions: [
            ...(logInfo?.permissions ?? []),
            {
              type: 'org',
              principal: values.organization,
              principalName: orgName,
              permissions: {
                readTubes: values.tubes,
                readPies: values.pieCharts,
                readPressures: values.idleVsPressure,
                readAnnotations: values.annotations,
                readTimestamps: values.timestamps,
                readConfiguration: values.configurations,
              },
            },
          ],
        };
        await logsRequest('logs share', request);
      } catch (e) {
        console.error('Failed to share log to org:', e);
        throw e;
      }
    };
  }, [logInfo?.permissions, logsRequest, params.id, showShare]);

  const onGuestShare = useMemo(() => {
    if (!showShare) return undefined;
    return async (values: GuestShareFormData) => {
      try {
        const request: ShareViaEmailRequest = {
          jobId: params.id,
          emailAddress: values.emailAddress,
          givenName: values.firstName,
          familyName: values.lastName,
          externalOrgName: values.externalOrgName,
          expiresOn: calculateNDaysFromNow(values.daysToExpiration),
          locale: navigator.language,
          permissions: {
            readTubes: values.tubes,
            readPies: values.pieCharts,
            readPressures: values.idleVsPressure,
            readIdleVsPressure: values.idleVsPressure,
            readAnnotations: values.annotations,
            readTimestamps: values.timestamps,
            readConfiguration: values.configurations,
          },
        };
        await logsRequest('logs share-via-email', request);
        console.log('successfully shared log to guest');
        sendEventUserSharedJobReport(logInfo, user, values);
      } catch (e) {
        console.error('Failed to share log to guest:', e);
        throw e;
      }
    };
  }, [logInfo, logsRequest, params.id, showShare, user]);

  const calculateNDaysFromNow = (daysToExpiration: string) => {
    let numberOfDaysToExpiration = Number(daysToExpiration);
    if (isNaN(numberOfDaysToExpiration)) {
      numberOfDaysToExpiration = SHORT_EXPIRATION_IN_DAYS;
    }

    return Date.now() + numberOfDaysToExpiration * MILLISECONDS_IN_ONE_DAY;
  };

  const isShared = (logInfo?.permissions?.length ?? 0) > 0 || (logInfo?.emailGuestShares?.length ?? 0) > 0;
  const showUnshare = displayPermissions.showShareActions && isShared;
  const onUnshare = useMemo(() => {
    if (!showUnshare) return undefined;
    return async (unshared: string[]) => {
      try {
        if (!logInfo) throw new Error('Cannot unshare log, log info not loaded');

        // We expect that the key will either be an organization id (org share) or an email address followed by a space,
        // followed by a timestamp (email share). We'll split the key to determine which is which. The goal of the next
        // four statements is to filter out the `permissions` and `emailGuestShares` that are being removed. For better
        // or for worse, the frontend is responsible for that, rather than just sending the id's of what is being removed
        // to be backend.
        const unsharedPrincipals = unshared.filter(key => key.split(' ').length === 1);
        const permissions = logInfo.permissions?.filter(p => !unsharedPrincipals.includes(p.principal)) ?? [];

        const unsharedEmailGuests = unshared.filter(key => key.split(' ').length === 2);
        const emailGuestShares =
          logInfo.emailGuestShares?.filter(e => !unsharedEmailGuests.includes(`${e.emailAddress} ${e.expiresOn}`)) ??
          [];

        await updateLog({ _id: params.id, update: { $set: { permissions, emailGuestShares } } });

        // Now we need to get the list of email addresses and org names that have been removed that will appear in the
        // success message that is displayed for the user.
        const removed = logInfo.permissions?.filter(p => unsharedPrincipals.includes(p.principal)) ?? [];
        const removedOrgNames = removed.map(r => r.principalName);
        const removedEmailGuests =
          logInfo.emailGuestShares!.filter(e => unsharedEmailGuests.includes(`${e.emailAddress} ${e.expiresOn}`)) ?? [];
        const removedEmailAddresses = removedEmailGuests.map(e => e.emailAddress);

        for (const emailAddress of removedEmailAddresses) {
          await sendEventUserUnsharedEmailShare(logInfo, user, emailAddress);
        }

        // Now that we have each of those, use the spread operator to get a straight list.
        return { names: [...removedOrgNames, ...removedEmailAddresses] };
      } catch (e) {
        console.error('Failed to unshare log:', e);
        throw e;
      }
    };
  }, [logInfo, params.id, showUnshare, updateLog, user]);

  // Only allow edit if online (!isLimited), is completed, and user is in correct role/owns the log
  const showUpdate = !isLimited && canUpdateLog;

  const showDownload = !!logInfo && displayPermissions.showDownloadActions;
  const onDownload = useMemo(() => {
    if (!showDownload) return undefined;
    return async () => {
      const name = logInfo.originalFileName || logInfo.name;
      try {
        await downloadLogFile(params.id, name);
      } catch (e) {
        console.error('Could not download log file:', e);
        throw e;
      }
    };
  }, [logInfo?.name, logInfo?.originalFileName, params.id, showDownload]);

  const showPrint = displayPermissions.showPrintActions;
  const onPrint = useMemo(() => {
    if (!showPrint) return undefined;
    return (options: JobDetailsPrintOptions) => {
      try {
        const id = params.id;
        if (!id || !options) throw new Error('Invalid data provided to onPrint');

        const printParams = getPrintQueryParams(options);
        printParams.append('startTime', timeRange.min.toString());
        printParams.append('endTime', timeRange.max.toString());

        // Gives print dialog time to close before navigating to print page.
        setTimeout(() => {
          history.push({
            pathname: AppRouteConstants.LogPrint.replace(':id', id),
            search: printParams.toString(),
          });
        }, 1000);
      } catch (e) {
        console.error('Could not open print page:', e);
        throw e;
      }
    };
  }, [history, params.id, showPrint, timeRange.max, timeRange.min]);

  return (
    <AnnotationsContextProvider parentId={params.id}>
      <JobInfo
        info={logInfo}
        data={data?.data}
        description={logInfo?.description || logInfo?.name || logInfo?.originalFileName}
        subheading={formattedName}
        siteName={logInfo?.siteName}
        heatExchangerModelNumber={logInfo?.heatExchangerModelNumber}
        orgName={org?.orgName}
        timezone={logInfo?.deviceTimezone || 'UTC'}
        fetchProgress={loadProgress}
        status={logInfo?.status ?? 'complete'}
        isLimitedMode={isLimitedMode}
        onComplete={onComplete}
        showUpdateButton={showUpdate}
        onDelete={onDeleteLog}
        onFamilyOrgShare={onFamilyOrgShare}
        onGuestShare={onGuestShare}
        onUnshare={onUnshare}
        onDownload={onDownload}
        onPrint={onPrint}
        hasErrors={!!data?.hasParseErrors}
        displayPermissions={displayPermissions}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
    </AnnotationsContextProvider>
  );
};
